import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Info, Loader2, X } from 'lucide-react';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import {
  adminDoa,
  type DoaResponsibilityEditable,
  type DoaResponsibilityRow,
} from '@/api/client';

/**
 * Sprint-16 — Edit / Create dialog for a DoA responsibility.
 *
 * Four editable fields:
 *   - label        (required, max 255)
 *   - definition   (long prose, surfaced as tooltip + audit context)
 *   - sort_order   (drives column order in the grid)
 *   - is_active    (soft-deactivate toggle)
 *
 * Code is captured only on create — immutable thereafter (junction rows
 * reference it as a natural key, so renaming would orphan history).
 *
 * Mirrors the eISF DocumentTypeFormDialog pattern but narrower (no
 * approver list, no JSON arrays).
 */

type Mode = 'edit' | 'create';

interface Props {
  mode: Mode;
  /** Required when mode === 'edit'. */
  code?: string;
  onClose: () => void;
  onSaved: (row: DoaResponsibilityRow) => void;
}

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

type FormShape = DoaResponsibilityEditable & { code: string };

const BLANK: FormShape = {
  code: '',
  label: '',
  definition: '',
  sort_order: 100,
  is_active: true,
  visible_to_site_users: false, // F5 default: hidden from site_user
};

export function ResponsibilityFormDialog({ mode, code, onClose, onSaved }: Props) {
  const existingQ = useQuery({
    queryKey: ['adminDoa', 'responsibility', code],
    queryFn: () => adminDoa.getResponsibility(code!),
    enabled: mode === 'edit' && !!code,
  });

  const [form, setForm] = useState<FormShape>(BLANK);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && existingQ.data) {
      const r = existingQ.data.responsibility;
      setForm({
        code: r.code,
        label: r.label,
        definition: r.definition,
        sort_order: r.sort_order,
        is_active: r.is_active,
        // F5 - server persists as 'true'|'false' STRING; convert.
        visible_to_site_users: r.visible_to_site_users === 'true',
      });
    }
  }, [mode, existingQ.data]);

  function set<K extends keyof FormShape>(k: K, v: FormShape[K]) {
    setForm((f) => ({ ...f, [k]: v }));
    setTouched(true);
  }

  const errors = useMemo(() => validate(form, mode), [form, mode]);
  const isValid = Object.keys(errors).length === 0;

  const createM = useMutation({
    mutationFn: () => adminDoa.createResponsibility(form),
    onSuccess: (r) => {
      toast.success(`"${r.responsibility.label}" added to the catalog`);
      onSaved(r.responsibility);
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Create failed'));
    },
  });

  const updateM = useMutation({
    mutationFn: () => {
      const { code: _code, ...patch } = form;
      void _code;
      return adminDoa.updateResponsibility(form.code, patch);
    },
    onSuccess: (r) => {
      toast.success(`"${r.responsibility.label}" updated`);
      onSaved(r.responsibility);
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Update failed'));
    },
  });

  function submit() {
    if (!isValid) {
      setTouched(true);
      return;
    }
    if (mode === 'create') createM.mutate();
    else updateM.mutate();
  }

  const isSaving = createM.isPending || updateM.isPending;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative my-8 w-full max-w-xl rounded-lg border border-surface-border bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-surface-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink-primary truncate">
              {mode === 'create' ? 'Add responsibility' : `Edit — ${form.label || existingQ.data?.responsibility.label || ''}`}
            </h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Master data — changes apply to every tenant and every grid.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-ink-muted hover:text-ink-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {mode === 'edit' && existingQ.isLoading && (
          <div className="flex items-center justify-center gap-2 p-10 text-ink-muted text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {mode === 'edit' && existingQ.isError && (
          <div className={`m-5 flex items-start gap-2 rounded border p-3 text-xs ${errorToneClass(existingQ.error)}`}>
            {isPermissionError(existingQ.error) ? <Info className="mt-0.5 h-3.5 w-3.5 shrink-0"/> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <span>
              {errorText(existingQ.error, 'Failed to load.')}
            </span>
          </div>
        )}

        {(mode === 'create' || existingQ.data) && (
          <>
            <div className="px-5 py-4 space-y-3">
              <Field label="Label" required error={touched ? errors.label : undefined}>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => set('label', e.target.value)}
                  className="form-input"
                  placeholder="e.g. Administer Study Intervention"
                />
              </Field>
              <Field
                label="Code (slug)"
                required
                hint={mode === 'edit'
                  ? 'Immutable — referenced by every junction row pointing at this responsibility'
                  : 'Lowercase kebab-case. e.g. administer-study-intervention'}
                error={touched ? errors.code : undefined}
              >
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => set('code', e.target.value.toLowerCase())}
                  readOnly={mode === 'edit'}
                  className={`form-input ${mode === 'edit' ? 'bg-surface-muted/50 cursor-not-allowed' : ''}`}
                  placeholder="administer-study-intervention"
                />
              </Field>
              <Field
                label="Definition"
                hint="Long-form description. Surfaced as a tooltip in the grid header and recorded in the audit trail for inspector clarity."
              >
                <textarea
                  value={form.definition}
                  onChange={(e) => set('definition', e.target.value)}
                  rows={4}
                  className="form-input"
                  placeholder="What this responsibility entails, who typically holds it, any §4.1.5 / GCP citations."
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Sort order"
                  hint="Ascending. Lower numbers display first in the grid."
                  error={touched ? errors.sort_order : undefined}
                >
                  <input
                    type="number"
                    min={0}
                    max={100000}
                    value={form.sort_order}
                    onChange={(e) => set('sort_order', Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="form-input"
                  />
                </Field>
                <Field
                  label="Active"
                  hint="Inactive responsibilities stay in the catalog but hide from new grids."
                >
                  <label className="inline-flex items-center gap-2 cursor-pointer pt-2">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => set('is_active', e.target.checked)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm text-ink-secondary">Visible in new grids</span>
                  </label>
                </Field>
              </div>

              {/* F5 - visible-to-site-users toggle */}
              <Field
                label="Visible to site users"
                hint="When on, staff who hold this responsibility surface on the Staff Documentation page for the site_user role. Default off."
              >
                <label className="inline-flex items-center gap-2 cursor-pointer pt-2">
                  <input
                    type="checkbox"
                    checked={!!form.visible_to_site_users}
                    onChange={(e) => set('visible_to_site_users', e.target.checked)}
                    data-testid="doa-responsibility-visible-site-users"
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-ink-secondary">Publish this responsibility to site users</span>
                </label>
              </Field>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-surface-border px-5 py-3">
              <div className="text-xs text-ink-muted flex items-center gap-1.5">
                <Info className="h-3 w-3" />
                {mode === 'create' ? 'New responsibilities are available to every tenant immediately.' : 'Changes propagate immediately.'}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onClose} disabled={isSaving} className="rounded border border-surface-border px-3 py-1.5 text-sm font-medium text-ink-primary hover:bg-surface-muted/50 disabled:opacity-60">
                  Cancel
                </button>
                <button type="button" onClick={submit} disabled={isSaving || (touched && !isValid)} className="inline-flex items-center gap-1.5 rounded bg-accent-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-60">
                  {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {mode === 'create' ? 'Create' : 'Save changes'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <style>{`
        .form-input { display:block; width:100%; border:1px solid var(--surface-border,#d8dce0); border-radius:6px; padding:8px 10px; font-size:13.5px; color:#2c3034; background:#fff; outline:none; }
        .form-input:focus { border-color:#2f6fd6; box-shadow:0 0 0 2px rgba(47,111,214,0.15); }
        .form-input[readonly] { color:#6b7280; }
      `}</style>
    </div>,
    document.body,
  );
}

function Field({
  label,
  hint,
  required,
  error,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-secondary">
        {label}
        {required && <span className="text-danger-600 ml-0.5">*</span>}
      </span>
      <div className="mt-1">{children}</div>
      {hint && !error && <div className="mt-1 text-[11px] text-ink-muted">{hint}</div>}
      {error && (
        <div className="mt-1 text-[11px] text-danger-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />{error}
        </div>
      )}
    </label>
  );
}

function validate(form: FormShape, mode: Mode): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!form.label.trim()) errs.label = 'Required';
  if (mode === 'create') {
    const slug = form.code.trim();
    if (!slug) errs.code = 'Required';
    else if (!SLUG_PATTERN.test(slug)) {
      errs.code = 'Must be lowercase kebab-case (a-z, 0-9, hyphen)';
    }
  }
  if (form.sort_order < 0 || form.sort_order > 100000) {
    errs.sort_order = 'Must be between 0 and 100000';
  }
  return errs;
}
